import { useFlaggingCallback } from '@databases/banners';
import { EditComponentProps } from '@databases/fields/edit/types';
import { defaultStoreUtils } from '@databases/redux';
import { DatabaseFieldType, SelectOption } from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import { MultiChange, MultiValueChangeType } from '@databases/utils/database/changesets';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import React, { FunctionComponent } from 'react';

import SelectPicker from '../../SelectPicker';

const SelectValueEdit: FunctionComponent<
  EditComponentProps<DatabaseFieldType.SELECT>
> = ({
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  value,
  field,
  addChangeToDraft,
  contentId,
  placeholder,
  appearance,
}) => {
  const canCreate = defaultStoreUtils.useSelector((state) => {
    const database =
      contentId != null
        ? selectDatabaseByContentIdRequired(state, { contentId })
        : undefined;
    return database && database.operations.updateMetadata && !database.meta.isLocked;
  });

  const handleCreate = useFlaggingCallback(
    (option: SelectOption, allSelectedValues: string[]) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      addChangeToDraft!({
        type: DatabaseChangeType.SET_SELECT_OPTIONS,
        fieldId: field.id,
        newValue: [...field.settings.selectOptions, option],
        change: {
          type: MultiValueChangeType.APPEND,
          value: option,
        },
      });

      const delta: MultiChange<DatabaseFieldType.SELECT> = field.settings.multi
        ? {
            type: MultiValueChangeType.APPEND,
            value: option.id ?? option.label,
          }
        : {
            type: MultiValueChangeType.SET_TO,
            value: [option.id ?? option.label],
          };

      onChange(allSelectedValues, {
        type: DatabaseFieldType.SELECT,
        delta,
      });
    },
    [
      addChangeToDraft,
      field.id,
      field.settings.multi,
      field.settings.selectOptions,
      onChange,
    ]
  );

  const handleSelect = useFlaggingCallback(
    async (addedValue: string, allSelectedValues: string[]) => {
      const delta: MultiChange<DatabaseFieldType.SELECT> = field.settings.multi
        ? {
            type: MultiValueChangeType.APPEND,
            value: addedValue,
          }
        : {
            type: MultiValueChangeType.SET_TO,
            value: [addedValue],
          };

      await onChange(allSelectedValues, {
        type: DatabaseFieldType.SELECT,
        delta,
      });
    },
    [onChange, field.settings.multi]
  );

  const handleRemove = useFlaggingCallback(
    async (removedValues: string[], allSelectedValues: string[]) => {
      await onChange(allSelectedValues, {
        type: DatabaseFieldType.SELECT,
        delta: {
          type: MultiValueChangeType.REMOVE,
          index: value.indexOf(removedValues[0]), // FIXME: index?
        },
      });
    },
    [onChange, value]
  );

  return (
    <SelectPicker
      value={value}
      options={field.settings.selectOptions}
      onFocus={onFocus}
      onBlur={onBlur}
      autoFocus={autoFocus}
      isMulti={field.settings.multi}
      isCreatable={canCreate && addChangeToDraft && field.settings.creatable !== false}
      onCreate={handleCreate}
      onSelect={handleSelect}
      onRemove={handleRemove}
      placeholder={placeholder}
      appearance={appearance}
    />
  );
};

export default SelectValueEdit;
